import { supabase } from '../client';
import { requireContext, type ActiveContext } from '../activeContext';
import type { Database } from '../generated';

export type WholesalerRecipient =
  Database['wholesalers']['Tables']['wholesaler_recipients']['Row'];
export type WholesalerRecipientInsert =
  Database['wholesalers']['Tables']['wholesaler_recipients']['Insert'];
export type WholesalerRecipientUpdate =
  Database['wholesalers']['Tables']['wholesaler_recipients']['Update'];

/**
 * List recipients attached to a wholesaler.
 *
 * Returns active rows by default. Per the pagination-required rule, this
 * caps at 100 rows -- a single wholesaler with more than that should reach
 * for a keyset list endpoint instead. We haven't seen one in the wild yet,
 * so we keep the simpler bounded query for now.
 */
export async function listRecipients(
  ctx: ActiveContext,
  wholesalerId: string,
  opts: { includeArchived?: boolean } = {},
): Promise<WholesalerRecipient[]> {
  requireContext(ctx);
  let q = supabase()
    .schema('wholesalers')
    .from('wholesaler_recipients')
    .select('*')
    .eq('wholesaler_id', wholesalerId)
    .order('updated_at', { ascending: false })
    .limit(100);
  if (!opts.includeArchived) q = q.neq('status', 'archived');
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function getRecipient(
  ctx: ActiveContext,
  id: string,
): Promise<WholesalerRecipient | null> {
  requireContext(ctx);
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_recipients')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export interface CreateRecipientInput {
  wholesalerId: string;
  displayName: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
  notes?: string | null;
}

export async function createRecipient(
  ctx: ActiveContext,
  input: CreateRecipientInput,
): Promise<WholesalerRecipient> {
  const { orgId, storeId } = requireContext(ctx);
  const displayName = input.displayName.trim();
  if (!displayName) throw new Error('Recipient needs a display name.');
  const insert: WholesalerRecipientInsert = {
    org_id: orgId,
    store_id: storeId,
    wholesaler_id: input.wholesalerId,
    display_name: displayName,
    contact_name: input.contactName ?? undefined,
    email: input.email ?? undefined,
    phone: input.phone ?? undefined,
    line1: input.line1 ?? undefined,
    line2: input.line2 ?? undefined,
    city: input.city ?? undefined,
    region: input.region ?? undefined,
    postal_code: input.postalCode ?? undefined,
    country: input.country ?? 'US',
    notes: input.notes ?? undefined,
    status: 'active',
  };
  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_recipients')
    .insert(insert)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerRecipient;
}

export interface UpdateRecipientInput {
  id: string;
  displayName?: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
  notes?: string | null;
  status?: WholesalerRecipient['status'];
}

export async function updateRecipient(
  ctx: ActiveContext,
  input: UpdateRecipientInput,
): Promise<WholesalerRecipient> {
  requireContext(ctx);
  const patch: WholesalerRecipientUpdate = {};
  if (input.displayName !== undefined) patch.display_name = input.displayName.trim();
  if (input.contactName !== undefined) patch.contact_name = input.contactName;
  if (input.email !== undefined) patch.email = input.email;
  if (input.phone !== undefined) patch.phone = input.phone;
  if (input.line1 !== undefined) patch.line1 = input.line1;
  if (input.line2 !== undefined) patch.line2 = input.line2;
  if (input.city !== undefined) patch.city = input.city;
  if (input.region !== undefined) patch.region = input.region;
  if (input.postalCode !== undefined) patch.postal_code = input.postalCode;
  if (input.country !== undefined) patch.country = input.country ?? 'US';
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.status !== undefined) patch.status = input.status;

  const { data, error } = await supabase()
    .schema('wholesalers')
    .from('wholesaler_recipients')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as WholesalerRecipient;
}

export async function archiveRecipient(ctx: ActiveContext, id: string): Promise<void> {
  await updateRecipient(ctx, { id, status: 'archived' });
}
